import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { useLocale } from '../contexts/LocaleContext.jsx';
import { digestSections, useShowDigest } from '../hooks/useShowDigest.js';
import styles from '../styles/ShowDigestPage.module.css';

/** Catalog keys of the missed-opportunity item kinds (ENG-202). */
const MISSED_KIND_KEYS = {
  unacknowledged_insight: 'showDigest.missed.kinds.unacknowledgedInsight',
  unthanked_tipper: 'showDigest.missed.kinds.unthankedTipper',
  unplayed_request: 'showDigest.missed.kinds.unplayedRequest',
  unwelcomed_first_timer: 'showDigest.missed.kinds.unwelcomedFirstTimer',
  unaddressed_dip: 'showDigest.missed.kinds.unaddressedDip',
};

/** One section of the digest, with its empty state when it has no items. */
const Section = ({ id, heading, empty, children, count }) => (
  <section className={styles.section} aria-labelledby={id} data-testid={id}>
    <h2 id={id} className={styles.sectionHeading}>{heading}</h2>
    {count === 0 ? <p className={styles.empty}>{empty}</p> : <ul className={styles.list}>{children}</ul>}
  </section>
);

/** A talking point or memory event: the insight's headline and detail. */
const InsightItem = ({ insight }) => (
  <li className={styles.item}>
    <p className={styles.itemTitle}>{insight.title}</p>
    {insight.body && <p className={styles.itemBody}>{insight.body}</p>}
  </li>
);

const Stat = ({ label, value }) => (
  <div className={styles.stat}>
    <dt className={styles.statLabel}>{label}</dt>
    <dd className={styles.statValue}>{value}</dd>
  </div>
);

/**
 * The musician's post-show digest of one show (ENG-446), from
 * `GET /shows/{showID}/digest` (ENG-193): a few numbers about the show, then
 * its talking points, memory events and missed opportunities, each with an
 * empty state. Reached from the past events list, from the dashboard right
 * after a stream ends, and from the "show report" inbox notification, whose
 * link is this route (`/performers/{band}/shows/{show}/report`).
 *
 * While the digest is still being assembled the page says so and checks
 * again on its own; a failed or missing digest offers Regenerate. The route
 * is for band members only, as the API is; a 403 is still shown as such.
 *
 * Listener names are plain text for now. They become buttons that open the
 * regulars context card once that card (ENG-434) and the user ids on digest
 * items (ENG-204) exist.
 */
const ShowDigestPage = () => {
  const { bandid, showId } = useParams();
  const { t, format } = useLocale();
  const { phase, digest, errorMessage, reload, regenerate, regenerating, regenerateError } = useShowDigest(showId);
  const eventsPath = `/performers/${bandid}/events`;

  const regenerateButton = (
    <>
      <button type="button" className={styles.primaryButton} onClick={regenerate} disabled={regenerating} data-testid="digest-regenerate">
        {regenerating ? t('showDigest.actions.regenerating') : t('showDigest.actions.regenerate')}
      </button>
      {regenerateError && <p className={styles.error} role="alert">{t('showDigest.state.regenerateFailed')}</p>}
    </>
  );

  const stateMessage = (body, extra = null, role = 'status') => (
    <div className={styles.state} role={role} data-testid={`digest-${phase}`}>
      <p>{body}</p>
      {extra}
    </div>
  );

  let content;
  switch (phase) {
    case 'loading':
      content = stateMessage(t('showDigest.state.loading'));
      break;
    case 'pending':
      content = stateMessage(t('showDigest.state.pending'));
      break;
    case 'failed':
      content = stateMessage(
        errorMessage ? t('showDigest.state.failedWithReason', { reason: errorMessage }) : t('showDigest.state.failed'),
        regenerateButton,
        'alert',
      );
      break;
    case 'missing':
      content = stateMessage(t('showDigest.state.missing'), regenerateButton);
      break;
    case 'live':
      content = stateMessage(t('showDigest.state.live'));
      break;
    case 'notFound':
      content = stateMessage(t('showDigest.state.notFound'), <Link to={eventsPath}>{t('showDigest.actions.backToEvents')}</Link>, 'alert');
      break;
    case 'forbidden':
      content = stateMessage(t('showDigest.state.forbidden'), null, 'alert');
      break;
    case 'error':
      content = stateMessage(
        t('showDigest.state.error'),
        <button type="button" className={styles.secondaryButton} onClick={reload}>{t('common.actions.retry')}</button>,
        'alert',
      );
      break;
    default:
      content = null;
  }

  if (phase === 'ready' && digest) {
    const sections = digestSections(digest);
    const audience = digest.audience ?? {};
    const songs = digest.songs ?? {};
    const minutes = Math.round((Number(audience.durationSeconds) || 0) / 60);
    content = (
      <>
        <dl className={styles.stats} aria-label={t('showDigest.stats.label')}>
          <Stat label={t('showDigest.stats.duration')} value={t('showDigest.stats.minutes', { count: minutes, formatted: format.number(minutes) })} />
          <Stat label={t('showDigest.stats.uniqueViewers')} value={format.number(Number(audience.uniqueViewers) || 0)} />
          <Stat label={t('showDigest.stats.peakViewers')} value={format.number(Number(audience.peakViewers) || 0)} />
          <Stat label={t('showDigest.stats.songsPlayed')} value={format.number(Number(songs.playedCount) || 0)} />
          <Stat label={t('showDigest.stats.requests')} value={format.number(Number(songs.requestCount) || 0)} />
          <Stat label={t('showDigest.stats.tips')} value={format.number(Number(digest.tips?.count) || 0)} />
        </dl>

        <Section
          id="digest-talking-points"
          heading={t('showDigest.talkingPoints.heading')}
          empty={t('showDigest.talkingPoints.empty')}
          count={sections.talkingPoints.length}
        >
          {sections.talkingPoints.map((insight) => <InsightItem key={insight.id} insight={insight} />)}
        </Section>

        <Section
          id="digest-memory-events"
          heading={t('showDigest.memoryEvents.heading')}
          empty={t('showDigest.memoryEvents.empty')}
          count={sections.memoryEvents.length}
        >
          {sections.memoryEvents.map((insight) => <InsightItem key={insight.id} insight={insight} />)}
        </Section>

        <Section
          id="digest-missed-opportunities"
          heading={t('showDigest.missed.heading')}
          empty={t('showDigest.missed.empty')}
          count={sections.missedOpportunities.length}
        >
          {sections.missedOpportunities.map((item, index) => (
            <li className={styles.item} key={`${item.kind}-${item.at ?? ''}-${index}`}>
              <p className={styles.itemMeta}>
                <span className={styles.kind}>{MISSED_KIND_KEYS[item.kind] ? t(MISSED_KIND_KEYS[item.kind]) : t('showDigest.missed.kinds.other')}</span>
                {Number.isFinite(item.minute) && (
                  <span className={styles.minute}>{t('showDigest.missed.minute', { minute: format.number(item.minute) })}</span>
                )}
              </p>
              {item.viewer?.username && <p className={styles.listener}>{item.viewer.username}</p>}
              {item.summary && <p className={styles.itemTitle}>{item.summary}</p>}
              {item.followUp && <p className={styles.itemBody}>{t('showDigest.missed.followUp', { idea: item.followUp })}</p>}
            </li>
          ))}
        </Section>
        {(sections.missedTruncated || sections.insightsTruncated) && (
          <p className={styles.note}>{t('showDigest.truncated')}</p>
        )}
      </>
    );
  }

  const title = digest?.title;
  const endedAt = digest?.endedAt;
  return (
    <div className={styles.page} data-testid="show-digest-page">
      <header className={styles.header}>
        <p className={styles.kicker}>{t('showDigest.kicker')}</p>
        <h1 className={styles.title}>{title || t('showDigest.titleFallback')}</h1>
        {endedAt && <p className={styles.subtitle}>{t('showDigest.endedAt', { date: format.dateTime(endedAt, 'eventListing') })}</p>}
      </header>
      {content}
    </div>
  );
};

export default ShowDigestPage;
